import type { Light, Segment } from './types.js';

/** Saldos de Gesfincas de una comunidad (en euros) */
export interface BalanceFigures {
  bancos: number;
  caja: number;
  /** < 0: la comunidad debe a proveedores; > 0: saldo a favor / anticipos */
  proveedores: number;
  impagados: number;
  gastos12m: number;
}

const round = (n: number, d: number) => Math.round(n * 10 ** d) / 10 ** d;

/**
 * Métricas de tesorería (decisión de negocio del 16 oct 2026):
 * - F1 (meses cubiertos) = (Bancos + Caja − deuda a proveedores) / (Gastos_12M / 12). Los anticipos no suman.
 *   Disponible negativo → ≤ 0 (rojo siempre). Sin gasto y disponible ≥ 0 → null (sin base).
 * - F2 (%) = Impagados / Gastos_12M. Sin gasto o sin impagados → null.
 */
export function treasuryMetrics(b: BalanceFigures) {
  const deudaProveedores = Math.max(0, -b.proveedores);
  const disponible = b.bancos + b.caja - deudaProveedores;
  const gastoMes = b.gastos12m / 12;
  const f1 =
    disponible < 0
      ? Math.min(0, round(disponible / Math.max(gastoMes, 1), 1))
      : gastoMes > 0
        ? round(disponible / gastoMes, 1)
        : null;
  const f2 = b.gastos12m > 0 && b.impagados > 0 ? round((b.impagados / b.gastos12m) * 100, 1) : null;
  return { deudaProveedores, disponible, gastoMes, f1, f2 };
}

/** Fila del detalle de tesorería de una oficina */
export interface TreasuryRow extends BalanceFigures {
  communityId: string;
  communityName: string;
  code: string;
  segment: Segment | null;
  affId: string | null;
  affName: string | null;
  saldoAdministracion: number;
  deudaProveedores: number;
  disponible: number;
  gastoMes: number;
  f1: number | null;
  f1Light: Light | null;
  f2: number | null;
  f2Light: Light | null;
}

export interface TreasuryReport {
  officeId: string;
  /** Carga de Gesfincas (FechaCarga) usada en la foto */
  loadedAt: string | null;
  snapshotTakenAt: string | null;
  thresholds: { F1: { amber: number; red: number }; F2: { amber: number; red: number } };
  rows: TreasuryRow[];
  /** Comunidades de la oficina sin saldo en Gesfincas (sin «Código» o sin fila) */
  missing: { id: string; name: string; segment: Segment | null; hasCode: boolean }[];
}
